import * as T from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/** Linear-light budgets. Non-emissive surfaces MUST stay below the bloom threshold. */
export const ART = Object.freeze({
  surfaceCeiling: .86, bloomThreshold: 1.15, bloomStrength: .48, bloomRadius: .28,
  exposure: 1, keyIntensity: 2.05, ambientIntensity: .85, rimIntensity: .65,
  outlinePixels: 1.15, actorScale: .79
});
const ramp = new T.DataTexture(new Uint8Array([55, 113, 184, 245]), 4, 1, T.RedFormat);
ramp.minFilter = ramp.magFilter = T.NearestFilter;
ramp.generateMipmaps = false;
ramp.needsUpdate = true;
const materials = new Map();

/** Four-band diffuse, colored shadow lift, bounded graphic highlights and a clean rim. */
export function toonMaterial(color, metal = 0, glow = 0, vertexColors = false) {
  const key = `${color}:${metal}:${glow}:${vertexColors}`;
  if (materials.has(key)) return materials.get(key);
  const mat = new T.MeshToonMaterial({color, gradientMap: ramp, vertexColors,
    emissive: color, emissiveIntensity: glow >= .35 ? glow * 5 : glow * .4});
  mat.userData.holiduelToon = true;
  mat.userData.surfaceCeiling = ART.surfaceCeiling;
  mat.onBeforeCompile = shader => {
    shader.uniforms.uMetal = {value: metal};
    shader.uniforms.uSurfaceCeiling = {value: ART.surfaceCeiling};
    shader.fragmentShader = `uniform float uMetal;\nuniform float uSurfaceCeiling;\n` + shader.fragmentShader;
    shader.fragmentShader = shader.fragmentShader.replace('#include <opaque_fragment>', `
      vec3 baseLight = max(outgoingLight - totalEmissiveRadiance, vec3(0.0));
      // A cool material-relative shadow tint, not a white ambient wash.
      float lit = dot(baseLight, vec3(.2126, .7152, .0722));
      baseLight *= mix(vec3(.76, .86, 1.0), vec3(1.0), smoothstep(.04, .32, lit));
      float facing = max(dot(normalize(normal), normalize(vViewPosition)), 0.0);
      float rim = smoothstep(.54, .81, 1.0 - facing);
      baseLight += rim * mix(diffuseColor.rgb, vec3(.12, .28, .38), .3) * .17;
      // Toy-metal highlights remain small and below the HDR bloom threshold.
      float spec = pow(max(dot(normalize(normal), normalize(vec3(-.35, .65, .70))), 0.0), 24.0);
      baseLight += smoothstep(.55, .73, spec) * uMetal * vec3(.16, .19, .20);
      float peak = max(baseLight.r, max(baseLight.g, baseLight.b));
      baseLight *= min(1.0, uSurfaceCeiling / max(peak, .0001));
      outgoingLight = baseLight + totalEmissiveRadiance;
      #include <opaque_fragment>
    `);
  };
  mat.customProgramCacheKey = () => 'holiduel-toon-v2';
  materials.set(key, mat);
  return mat;
}

export function lightScene(scene) {
  scene.add(new T.HemisphereLight('#b5d8ef', '#293c64', ART.ambientIntensity));
  const key = new T.DirectionalLight('#fff2db', ART.keyIntensity);
  key.position.set(-8, 18, 11); key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  Object.assign(key.shadow.camera, {left:-25, right:25, top:25, bottom:-25, far:75});
  key.shadow.normalBias = .035; key.shadow.bias = -.00015; key.shadow.radius = 2;
  scene.add(key);
  const rim = new T.DirectionalLight('#7fb6ff', ART.rimIntensity);
  rim.position.set(7, 6, -9); scene.add(rim);
  return key;
}

const outlineUniforms = {viewport: {value: new T.Vector2(1280, 800)}, thickness: {value: ART.outlinePixels}};
const outlineMaterial = new T.ShaderMaterial({
  name: 'Holiduel ink contours', side: T.BackSide, uniforms: outlineUniforms,
  vertexShader: `uniform vec2 viewport; uniform float thickness;
    void main(){
      vec4 p = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      vec3 n = normalize(normalMatrix * normal);
      vec2 direction = (projectionMatrix * vec4(n, 0.0)).xy;
      float len = length(direction);
      if(len > .001) p.xy += direction / len * thickness * 2.0 / viewport * p.w;
      p.z += .00006 * p.w;
      gl_Position = p;
    }`,
  fragmentShader: `void main(){
    gl_FragColor = vec4(.008, .021, .036, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }`
});
export function setOutlineViewport(width, height, pixels = ART.outlinePixels) {
  outlineUniforms.viewport.value.set(Math.max(1,width), Math.max(1,height));
  outlineUniforms.thickness.value = pixels;
}
/** One hull per animated bone, rather than another draw call for every material. */
export function addContour(group) {
  group.updateMatrixWorld(true);
  const inv = group.matrixWorld.clone().invert(), parts = [];
  group.traverse(o => {
    if (!o.isMesh || o.isInstancedMesh || o.userData.inkContour) return;
    const g = o.geometry.index ? o.geometry.toNonIndexed() : o.geometry.clone();
    for (const attribute of Object.keys(g.attributes)) if (!['position','normal'].includes(attribute)) g.deleteAttribute(attribute);
    g.applyMatrix4(inv.clone().multiply(o.matrixWorld)); parts.push(g);
  });
  if (!parts.length) return;
  const geometry = mergeGeometries(parts);
  parts.forEach(g => g.dispose());
  if (!geometry) return;
  const hull = new T.Mesh(geometry, outlineMaterial);
  hull.name = 'ink-contour'; hull.userData.inkContour = true;
  hull.castShadow = hull.receiveShadow = false;
  group.add(hull);
}

let shadowTexture;
export function contactShadow(width = 1.8, depth = width, opacity = .32) {
  if (!shadowTexture) {
    const size = 64, data = new Uint8Array(size * size * 4);
    for(let y=0;y<size;y++) for(let x=0;x<size;x++){
      const d = Math.hypot((x+ .5)/size*2-1, (y+.5)/size*2-1), i=(y*size+x)*4;
      data[i]=data[i+1]=data[i+2]=255;
      data[i+3]=Math.round(Math.pow(Math.max(0,1-d),1.5)*255);
    }
    shadowTexture = new T.DataTexture(data,size,size);shadowTexture.needsUpdate=true;
    shadowTexture.magFilter=shadowTexture.minFilter=T.LinearFilter;
  }
  const m = new T.Mesh(new T.PlaneGeometry(width,depth),new T.MeshBasicMaterial({
    color:'#092640',map:shadowTexture,transparent:true,opacity,depthWrite:false,polygonOffset:true,polygonOffsetFactor:-1
  }));
  m.rotation.x=-Math.PI/2;m.position.y=.027;m.userData.ownedMaterial=true;
  return m;
}
